import { useState, useEffect, useCallback } from 'react';

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Tracks the dimensions of the target element.
 *
 * Uses offsetWidth/offsetHeight for stable layout dimensions.
 * Re-measures on ResizeObserver events.
 */
export function useContainerSize(targetRef: React.RefObject<HTMLElement | null>): ContainerSize {
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const w = (el as HTMLElement).offsetWidth;
    const h = (el as HTMLElement).offsetHeight;
    setSize((prev) => {
      if (prev.width === w && prev.height === h) return prev;
      return { width: w, height: h };
    });
  }, [targetRef]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure());
      observer.observe(el);
      return () => observer.disconnect();
    }
  }, [targetRef, measure]);

  return size;
}
